var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import * as React from "react";
import { Link } from "react-router-dom";
import { Checkbox, Dimmer, Form, Grid, Header, Loader, Table } from 'semantic-ui-react';
import { AppPaths, UiConfigContext } from "../../AppConfig";
import { ORIGIN } from "../../Config";
import ErrorDisplay from "../../ErrorDisplay";
import { Help } from "../../Help";
import { postJsonWithAuthn } from "../../RequestHelpers";
import { buildOnComponentStateChangeHandler } from "../FormState";
import { getFishAmplifierCatalog, HcrAmplifier, LiquidVolume } from "../rna-cish/RnaCatalog";
import BufferForm from "./BufferForm";
import { HcrAmplifierForm } from "./HcrAmplifierForm";
import KitSerializer from "./KitSerializer";
import { ErrorElements } from "./NewRnaFishKitPage";
import ProbeSetForm, { EditableOrganism, EditableTarget, } from "./ProbeSetForm";
import SelectSampleTypeForm from "./SelectSampleTypeForm";
export var defaultProbeSetState = function (catalog, legacy, manualPro) { return ({
    // leave entry blank -- not selected
    organism: EditableOrganism.default(),
    target: EditableTarget.default(catalog.probeSet.custom.probeSetSizes[0]),
    hcrAmplifier: manualPro ? new HcrAmplifier("X2") : getFishAmplifierCatalog(legacy, catalog).defaultChannel.hcrAmplifier,
    scale: catalog.probeSet.catalog.fishDefaultScale,
    searchLoading: false,
    targets: [],
    editSequenceOn: false,
    sequenceSaveButton: 'Copy',
    searchQuery: ""
}); };
export var defaultAmpState = function (catalog) { return ({
    hcrAmplifier: catalog.defaultChannel.hcrAmplifier,
    label: catalog.defaultChannel.defaultLabel.label,
    scale: catalog.defaultChannel.defaultLabel.defaultScale
}); };
export var defaultSampleTypeState = function () { return ({
    sampleType: {
        id: "",
        label: ""
    }
}); };
export var defaultBufferState = function (catalog, legacy, manualPro) { return ({
    amp: legacy ? catalog.legacyFishBuffer.amplifier.defaultVolumeMl : manualPro ? new LiquidVolume(0) : catalog.goldFishBuffer.amplifier.defaultVolumeMl,
    hybridization: legacy ? catalog.legacyFishBuffer.hybridization.defaultVolumeMl : catalog.goldFishBuffer.hybridization.defaultVolumeMl,
    wash: legacy ? catalog.legacyFishBuffer.wash.defaultVolumeMl : new LiquidVolume(0),
    goldWash: legacy ? new LiquidVolume(0) : catalog.goldFishBuffer.goldWash.defaultVolumeMl,
    goldAmpWash: legacy ? new LiquidVolume(0) : manualPro ? new LiquidVolume(0) : catalog.goldFishBuffer.goldAmpWash.defaultVolumeMl,
    proAmpWash: manualPro ? catalog.goldFishBuffer.proAmpWash.defaultVolumeMl : new LiquidVolume(0)
}); };
export var defaultFormState = function (catalog, legacy) { return ({
    ampEnabled: true,
    buffersEnabled: true,
    comment: '',
    errors: [],
    price: undefined,
    priceLoading: false,
    probeSetEnabled: true,
    ampState: defaultAmpState(getFishAmplifierCatalog(legacy, catalog)),
    bufferState: defaultBufferState(catalog, legacy, false),
    probeSetState: defaultProbeSetState(catalog, legacy, false),
    sampleTypeFormState: defaultSampleTypeState(),
    legacy: legacy
}); };
// The url path to use when the authenticating user's discounts should be applied
export var CART_PRICE_PATH = '/cart/price-kit';
/**
 * For this to show up, make sure to wrap the EditKitForm in a Form who sets `error` when errors is non empty
 */
var EditKitForm = /** @class */ (function (_super) {
    __extends(EditKitForm, _super);
    function EditKitForm(props) {
        var _this = _super.call(this, props) || this;
        _this.onPscStateChange = buildOnComponentStateChangeHandler(pscStateWrangler, _this.props.setFormState);
        _this.onAmpStateChange = buildOnComponentStateChangeHandler(ampStateWrangler, _this.props.setFormState);
        _this.onBufferStateChange = buildOnComponentStateChangeHandler(bufStateWrangler, _this.props.setFormState);
        _this.onSampleTypeStateChangeBaseHandler = buildOnComponentStateChangeHandler(sampleTypeStateWrangler, _this.props.setFormState);
        _this.onProbeSetEnableChange = function () {
            _this.props.setFormState(function (s) {
                return { probeSetEnabled: !s.probeSetEnabled };
            }, function (newState) {
                // if probe set was just enabled, set the amplifier component's amplifier
                if (newState.probeSetEnabled) {
                    // trigger an amplifier write with a no op state
                    _this.onProbeSetStateChange({}, _this.props.formState.probeSetState.hcrAmplifier, _this.onUserSelection);
                }
                else {
                    _this.onUserSelection();
                }
            });
        };
        _this.onAmplifierEnableChange = function () {
            _this.props.setFormState(function (s) {
                return { ampEnabled: !s.ampEnabled };
            }, function (newState) {
                if (newState.ampEnabled) {
                    _this.onAmpStateChange({}, _this.onUserSelection);
                }
                else {
                    _this.onUserSelection();
                }
            });
        };
        _this.onBuffersEnableChange = function () {
            _this.props.setFormState(function (s) {
                return { buffersEnabled: !s.buffersEnabled };
            }, function () {
                _this.onUserSelection();
            });
        };
        _this.onCommentChange = function (event) {
            var str = event.currentTarget.value;
            _this.props.setFormState(function () {
                return {
                    comment: str
                };
            });
        };
        _this.onProbeSetStateChange = function (updateObjOrFn, prevAmplifier, callback) {
            var getAppropriateAmplifierScale = function (hcrAmplifier, newState) {
                var scale;
                if (["B6", "B7", "B8", "B9", "B10"].includes(hcrAmplifier)) {
                    scale = getFishAmplifierCatalog(_this.props.legacy, _this.props.catalog).freeTextLabelScales
                        .find(function (s) { return s.valuePmol === newState.ampState.scale.valuePmol; });
                    if (scale === undefined) {
                        scale = getFishAmplifierCatalog(_this.props.legacy, _this.props.catalog).freeTextLabelScales[0];
                    }
                }
                else {
                    scale = newState.ampState.scale;
                }
                return scale;
            };
            var stateCalculator;
            if (typeof updateObjOrFn === "function") {
                stateCalculator = function (oldState) {
                    // apply the provided function
                    return updateObjOrFn(oldState.probeSetState);
                };
            }
            else {
                stateCalculator = function () {
                    // just return the plain object
                    return updateObjOrFn;
                };
            }
            _this.props.setFormState(function (oldState) { return ({
                probeSetState: Object.assign({}, oldState.probeSetState, stateCalculator(oldState))
            }); }, function (newState) {
                // propagate to amplifier component
                var _a;
                var channel = getFishAmplifierCatalog(_this.props.legacy, _this.props.catalog)
                    .channels.find(function (a) {
                    return a.hcrAmplifier.id === newState.probeSetState.hcrAmplifier.id;
                });
                if (channel == null) {
                    throw Error("Invalid probe channel");
                }
                var label = newState.ampState.label;
                if (newState.probeSetState.hcrAmplifier.id !== prevAmplifier.id) {
                    label = channel.defaultLabel.label;
                }
                // get a valid scale for the amplifier
                var validAmplifierScale = getAppropriateAmplifierScale(newState.probeSetState.hcrAmplifier.id, newState);
                // check if the valid amplifier scale matches a valid scale to the label we have selected
                var scale = (_a = channel.defaultLabel) === null || _a === void 0 ? void 0 : _a.scales.find(function (s) { return s.valuePmol === validAmplifierScale.valuePmol; });
                _this.onAmpStateChange({
                    hcrAmplifier: newState.probeSetState.hcrAmplifier,
                    label: label,
                    scale: scale ? scale : channel.defaultLabel.defaultScale
                }, callback);
            });
        };
        _this.onAmplifierStateChange = function (updateObjOrFn, callback) {
            var stateCalculator;
            if (typeof updateObjOrFn === "function") {
                stateCalculator = function (oldState) {
                    // apply the provided function
                    return updateObjOrFn(oldState.ampState);
                };
            }
            else {
                stateCalculator = function () {
                    // just return the plain object
                    return updateObjOrFn;
                };
            }
            _this.props.setFormState(function (oldState) { return ({
                ampState: Object.assign({}, oldState.ampState, stateCalculator(oldState))
            }); }, function (newState) {
                // propagate to probe set component
                _this.onPscStateChange({
                    hcrAmplifier: newState.ampState.hcrAmplifier
                }, callback);
            });
        };
        // Wrapper around normal state propagation to handle setting the wash buffer to 0 when northern blot is selected
        _this.onSampleTypeStateChange = function (sampleTypeFormState, prevSampleType, callback) {
            return _this.onSampleTypeStateChangeBaseHandler(sampleTypeFormState, function (newState) {
                var sampleType = newState.sampleTypeFormState.sampleType;
                if (sampleType.id === "NORTHERN_BLOT") {
                    _this.onBufferStateChange({
                        wash: new LiquidVolume(0),
                        goldWash: new LiquidVolume(0)
                    }, callback);
                }
                else if (prevSampleType.id === "NORTHERN_BLOT" && sampleType.id !== prevSampleType.id) {
                    _this.onBufferStateChange({
                        wash: _this.props.legacy
                            ? _this.props.catalog.legacyFishBuffer.wash.defaultVolumeMl : new LiquidVolume(0),
                        goldWash: !_this.props.legacy
                            ? _this.props.catalog.goldFishBuffer.goldWash.defaultVolumeMl : new LiquidVolume(0)
                    }, callback);
                }
                else {
                    if (callback !== undefined) {
                        callback();
                    }
                }
            });
        };
        // The user finished editing something
        _this.onUserSelection = function () {
            _this.loadPrice();
        };
        _this.loadPrice = function () {
            // start by setting the state so that the callback will see the state after previous changes have applied
            _this.props.setFormState(function () { return ({ priceLoading: true }); }, function (newState) {
                var kitBody = EditKitForm.serializeRnaFishKit(newState);
                postJsonWithAuthn(_this.props.kitPriceUrlPath, JSON.stringify(kitBody))
                    .then(function (resp) {
                    if (resp.status === 400) {
                        return resp.json().then(function (json) { return new ErrorElements(json.errors); });
                    }
                    return resp.json();
                }).then(function (resp) {
                    if (resp instanceof ErrorElements) {
                        _this.props.setFormState(function () { return ({
                            errors: resp.errors,
                            priceLoading: false,
                            price: undefined
                        }); });
                    }
                    else {
                        _this.props.setFormState(function () { return ({
                            errors: [],
                            price: resp,
                            priceLoading: false
                        }); });
                    }
                }).catch(function (rej) {
                    console.error("Couldn't update price: " + rej);
                });
            });
        };
        return _this;
    }
    EditKitForm.prototype.render = function () {
        return React.createElement("div", { className: 'kit-form' },
            React.createElement(ErrorDisplay, { errors: this.props.formState.errors, errorPath: "/" }),
            React.createElement("div", null),
            React.createElement(Header, { className: "component-slider" },
                React.createElement("span", { className: 'sample-type-span' }, "Sample type")),
            React.createElement("div", { className: 'no-outline-kit-component' },
                React.createElement(SelectSampleTypeForm, { sampleTypeState: this.props.formState.sampleTypeFormState, sampleTypes: this.props.legacy ? this.props.catalog.legacyFishBuffer.sampleTypes : this.props.catalog.goldFishBuffer.sampleTypes, manualPro: false, onSampleTypeChange: this.onSampleTypeStateChange, onUserSelection: this.onUserSelection, showErrors: this.props.showErrors, errors: this.props.formState.errors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.probeSetEnabled, otherComponentStates: [this.props.formState.ampEnabled, this.props.formState.buffersEnabled], onChange: this.onProbeSetEnableChange, label: this.props.legacy ? "HCR™ Probe (v3.0)" : "HCR™ HiFi Probe", cish: false },
                React.createElement(ProbeSetForm, { catalog: this.props.catalog, errors: this.props.formState.errors, enableCustomPrice: this.props.enableCustomPrice, enableCustomScale: this.props.enableProbeSetCustomScale, onProbeSetChange: this.onProbeSetStateChange, onUserSelection: this.onUserSelection, priceCents: this.props.formState.price ? this.props.formState.price.kitPrice.probeSetPriceCents : undefined, priceLoading: this.props.formState.priceLoading, probeSetState: this.props.formState.probeSetState, selectedSampleType: this.props.formState.sampleTypeFormState.sampleType, restrictCustomTargetProbeSetSizeToCatalog: this.props.restrictCustomTargetProbeSetSizeToCatalog, manualPro: false, legacy: this.props.legacy, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.ampEnabled, otherComponentStates: [this.props.formState.probeSetEnabled, this.props.formState.buffersEnabled], onChange: this.onAmplifierEnableChange, label: this.props.legacy ? "HCR™ Amplifier (v3.0)" : "HCR™ Gold Amplifier", cish: false },
                React.createElement(HcrAmplifierForm, { amplifierLabel: this.props.legacy ? EditKitForm.amplifierLabel : EditKitForm.goldAmplifierLabel, ampState: this.props.formState.ampState, catalog: getFishAmplifierCatalog(this.props.legacy, this.props.catalog), errors: this.props.formState.errors, hcrAmplifierDisabled: this.props.formState.probeSetEnabled, enableCustomPrice: this.props.enableCustomPrice, enableCustomScale: this.props.enableAmplifierCustomScale, onAmpStateChange: this.onAmplifierStateChange, selectedSampleType: this.props.formState.sampleTypeFormState.sampleType, onUserSelection: this.onUserSelection, priceCents: this.props.formState.price ? this.props.formState.price.kitPrice.amplifierPriceCents : undefined, priceLoading: this.props.formState.priceLoading, experimentVolumeTable: this.props.legacy ? ProbeSetForm.experimentVolumeTable : EditKitForm.goldExperimentVolumeTable, legacy: this.props.legacy, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.buffersEnabled, otherComponentStates: [this.props.formState.probeSetEnabled, this.props.formState.ampEnabled], onChange: this.onBuffersEnableChange, label: this.props.legacy ? "HCR™ Buffers (v3.0)" : "HCR™ Buffers", cish: false },
                React.createElement(BufferForm, { sampleType: this.props.formState.sampleTypeFormState.sampleType, legacy: this.props.legacy, manualPro: false, bufferState: this.props.formState.bufferState, catalog: this.props.legacy ? this.props.catalog.legacyFishBuffer : this.props.catalog.goldFishBuffer, enableCustomPrice: this.props.enableCustomPrice, enableCustomScale: this.props.enableBufferCustomScale, errors: this.props.formState.errors, onBufferChange: this.onBufferStateChange, onUserSelection: this.onUserSelection, priceCents: this.props.formState.price ? this.props.formState.price.kitPrice.buffersPriceCents : undefined, priceLoading: this.props.formState.priceLoading, showErrors: this.props.showErrors })),
            React.createElement(ErrorDisplay, { errors: this.props.formState.errors, errorPath: "/" }),
            React.createElement(Grid, { columns: 4, stackable: true },
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 12, className: "no-left-padding-mobile" },
                        React.createElement(Form.Field, null,
                            React.createElement("label", { htmlFor: 'comment' }, "Comment"),
                            React.createElement(Form.TextArea, { style: { fontFamily: 'Avenir Next' }, placeholder: 'Please share any instructions for probe design, ' +
                                    'order fulfillment, or other relevant details regarding this order. ' +
                                    'If you have a preferred transcriptome for probe design, ' +
                                    'let us know how we can access it.', value: this.props.formState.comment, onChange: this.onCommentChange }))),
                    priceColumn(React.createElement("div", { 
                        // apply padding that matches the kit component div padding
                        style: { 'paddingRight': '.5rem' } },
                        React.createElement(PriceDisplay, { loading: this.props.formState.priceLoading, priceCents: this.props.formState.price ? this.props.formState.price.kitPrice.subtotalCents : undefined })))),
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 16 }, this.props.formState.price && discountMessage(this.props.formState.price.discountCents, this.props.formState.price.listPriceCents)))));
    };
    EditKitForm.prototype.componentDidMount = function () {
        if (this.props.loadPriceOnMount) {
            this.loadPrice();
        }
    };
    EditKitForm.serializeRnaFishKit = function (formState) {
        var serializer = new KitSerializer();
        if (formState.probeSetEnabled) {
            serializer.setProbeSetState(formState.probeSetState);
        }
        if (formState.ampEnabled) {
            serializer.setAmplifier(formState.ampState);
        }
        if (formState.buffersEnabled) {
            serializer.setBuffers(formState.bufferState);
        }
        return serializer.serializeKit(formState.comment, formState.legacy, formState.sampleTypeFormState.sampleType);
    };
    EditKitForm.amplifierLabel = React.createElement(React.Fragment, null,
        "Amplifier ",
        React.createElement(Help, null, "For multiplex experiments, choose a different HCR\u2122 Amplifier (v3.0) (B1, B2, \u2026) for each target RNA that will be imaged in the same sample (for example, amplifier B1 for target 1, amplifier B2 for target 2, \u2026). Note that if your kit includes an HCR\u2122 Probe (v3.0), the HCR\u2122 Amplifier (v3.0) in the kit is fixed by the specification of the probe above using the dropdown \u201CFor use with amplifier\u201D."));
    EditKitForm.goldAmplifierLabel = React.createElement(React.Fragment, null,
        "Amplifier ",
        React.createElement(Help, null,
            React.createElement("p", null, "For multiplex experiments, choose a different HCR\u2122 Gold Amplifier (X1, X2, \u2026) for each target that will be imaged in the same sample (for example, amplifier X1 for target 1, amplifier X2 for target 2, \u2026). Note that if your kit includes an HCR\u2122 HiFi Probe, the amplifier in the kit is fixed by the specification of the probe above using the dropdown \u201CFor use with amplifier\u201D."),
            React.createElement("p", null, "For flexibility, amplifiers X1, X2, and X3 are available with multiple label options (488, 546, 647), while for expanded multiplexing, amplifiers X4\u2013X10 are each available with one label option. We recommend using a longer-wavelength label (e.g., 647 or 750) for targets that are more difficult to detect (e.g., low-expression target and/or short target sequence), as autofluorescence tends to be higher in shorter-wavelength channels (e.g., 405 or 488).")));
    EditKitForm.goldExperimentVolumeTable = React.createElement(Table, { celled: true, striped: true },
        React.createElement(Table.Header, null,
            React.createElement(Table.Row, null,
                React.createElement(Table.HeaderCell, null, "Sample type"),
                React.createElement(Table.HeaderCell, null, "Reaction volume (\u00B5L)"),
                React.createElement(Table.HeaderCell, null, "HCR\u2122 Gold Amplifier (\u00B5L)"),
                React.createElement(Table.HeaderCell, null, "Experiments"))),
        React.createElement(Table.Body, null,
            React.createElement(Table.Row, null,
                React.createElement(Table.Cell, null, "Sample on a slide (e.g., tissue section or adherent cells)"),
                React.createElement(Table.Cell, null, "100"),
                React.createElement(Table.Cell, null, "2"),
                React.createElement(Table.Cell, null, "100")),
            React.createElement(Table.Row, null,
                React.createElement(Table.Cell, null, "Sample in solution (e.g., whole-mount embryo or cells in suspension)"),
                React.createElement(Table.Cell, null, "500"),
                React.createElement(Table.Cell, null, "10"),
                React.createElement(Table.Cell, null, "20")),
            React.createElement(Table.Row, null,
                React.createElement(Table.Cell, null, "Northern blot"),
                React.createElement(Table.Cell, null, "2000"),
                React.createElement(Table.Cell, null, "40"),
                React.createElement(Table.Cell, null, "5"))));
    return EditKitForm;
}(React.PureComponent));
export default EditKitForm;
var PriceDisplay = /** @class */ (function (_super) {
    __extends(PriceDisplay, _super);
    function PriceDisplay() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    PriceDisplay.prototype.render = function () {
        return React.createElement("div", null,
            React.createElement(Loader, { active: this.props.loading, inline: true, size: 'mini' }),
            "\u00A0 Price (USD): ",
            this.props.priceCents === undefined ? ' (pending)' : '$' +
                formatMonetaryDecimal(this.props.priceCents));
    };
    return PriceDisplay;
}(React.PureComponent));
export { PriceDisplay };
var ToggleableKitComponent = /** @class */ (function (_super) {
    __extends(ToggleableKitComponent, _super);
    function ToggleableKitComponent(props) {
        return _super.call(this, props) || this;
    }
    ToggleableKitComponent.prototype.render = function () {
        return React.createElement(React.Fragment, null,
            !this.props.cish ?
                React.createElement(Header, { className: "component-slider" },
                    React.createElement(Grid, { columns: 2 },
                        React.createElement(Grid.Column, null,
                            React.createElement("span", null, this.props.label)),
                        React.createElement(Grid.Column, { textAlign: 'right' },
                            React.createElement(Checkbox, { checked: this.props.componentEnabled, 
                                // lock this one on if all the others are disabled
                                disabled: this.props.componentEnabled && this.props.otherComponentStates.every(function (b) { return !b; }), onChange: this.props.onChange, toggle: true })))) :
                React.createElement(Header, { className: "cish-component-slider" },
                    React.createElement(Grid, { columns: 2, stackable: true },
                        React.createElement(Grid.Row, null,
                            React.createElement(Grid.Column, null, this.props.componentEnabled && React.createElement("span", null, this.props.label)),
                            React.createElement(Grid.Column, { textAlign: 'right' },
                                React.createElement(Checkbox, { checked: this.props.componentEnabled, 
                                    // lock this one on if all the others are disabled
                                    disabled: (this.props.componentEnabled && this.props.otherComponentStates.every(function (b) { return !b; }) || this.props.disableToggle), 
                                    // zero width space label helps vertical alignment
                                    label: '\u00A0', onChange: this.props.onChange, toggle: true }))))),
            React.createElement(Dimmer.Dimmable, { dimmed: !this.props.componentEnabled, className: this.props.cish ? 'cish-admin-kit-component' : 'kit-component' },
                !this.props.cish ?
                    React.createElement(Dimmer, { active: !this.props.componentEnabled, inverted: true },
                        React.createElement(Header, null,
                            "No ",
                            this.props.label)) :
                    React.createElement(Dimmer, { className: 'dimmed-component', active: !this.props.componentEnabled, inverted: true },
                        React.createElement(Header, null,
                            "NO ",
                            this.props.label)),
                this.props.children));
    };
    return ToggleableKitComponent;
}(React.PureComponent));
export { ToggleableKitComponent };
export var priceColumn = function (priceNode) {
    return React.createElement(Grid.Column, { style: { 'position': 'relative' } },
        React.createElement("div", { className: 'price-column' }, priceNode));
};
export var discountMessage = function (discountCents, listPriceCents) {
    return React.createElement(UiConfigContext.Consumer, null, function (uiConfig) {
        if (discountCents > 0) {
            return React.createElement("h5", { className: 'discount-msg' },
                "This reflects a discount of $",
                formatMonetaryDecimal(discountCents),
                " off the list price of $",
                formatMonetaryDecimal(listPriceCents));
        }
        else if (!(uiConfig.authenticated)) {
            if (ORIGIN === "mi") {
                return React.createElement("p", { className: 'discount-msg' },
                    React.createElement(Link, { to: AppPaths.REGISTER }, "Register"),
                    " to see applicable discounts");
            }
            else {
                return React.createElement(React.Fragment, null);
            }
        }
        else {
            return React.createElement(React.Fragment, null);
        }
    });
};
export var formatMonetaryDecimal = function (cents) {
    if (cents < 0) {
        return "(" + (-cents / 100).toFixed(2) + ")";
    }
    return (cents / 100).toFixed(2);
};
var PscStateWrangler = /** @class */ (function () {
    function PscStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ probeSetState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.probeSetState; };
    }
    return PscStateWrangler;
}());
var AmpStateWrangler = /** @class */ (function () {
    function AmpStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ ampState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.ampState; };
    }
    return AmpStateWrangler;
}());
var BufferStateWrangler = /** @class */ (function () {
    function BufferStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ bufferState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.bufferState; };
    }
    return BufferStateWrangler;
}());
var SampleTypeStateWrangler = /** @class */ (function () {
    function SampleTypeStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ sampleTypeFormState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.sampleTypeFormState; };
    }
    return SampleTypeStateWrangler;
}());
var pscStateWrangler = new PscStateWrangler();
var ampStateWrangler = new AmpStateWrangler();
var bufStateWrangler = new BufferStateWrangler();
var sampleTypeStateWrangler = new SampleTypeStateWrangler();
//# sourceMappingURL=EditKitForm.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/kit/rna-fish/EditKitForm.js
// module id = 12
// module chunks = 0